<template>
  <div class="card card-height">
    <div class="table-container">
      <table class="table custom-table-padding">
        <thead>
          <tr>
            <th class="th-checkbox"></th>
            <th class="pl-10 particular">Item Details</th>
            <th>Service Resource</th>
            <th class="qty">Qty</th>
            <th class="rate">Rate</th>
            <th class="sub-amount">Amount</th>
            <th class="discount">Dis(%)</th>
            <th class="d-amount">Dis</th>
            <th class="after-amount">After Discount</th>
            <th class="total">Total Amount</th>
            <th class="th-action"></th>
          </tr>
        </thead>

        <tbody ref="tableRef">
          <RowInline
            v-for="(item, i) in tableItems"
            :key="i"
            :item="item"
            :index="i"
            :vatRate="vatRate"
            @onOpenEditItem="onOpenEditItem"
            @onDeleteItem="onDeleteItem"
            @addOrEditTeeth="openDentitionModal"
          />

          <tr>
            <td v-for="i in 10" :key="i"></td>
          </tr>
        </tbody>
      </table>
      <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
    </div>
    <div class="row mt-4 mb-5">
      <div class="col-md-5">
        <MoneyReceiptForm :grand-total="grandTotal" />
      </div>
      <div class="col-md-5 ml-auto">
        <div>
          <table class="table create-billing border table-totals">
            <tbody>
              <tr>
                <td class="product px-2">Sub-Total Amount</td>
                <td class="comma-sep">:</td>
                <td class="price text-right px-2">{{ subTotal }}</td>
              </tr>
              <tr>
                <td class="product px-2">Total VAT</td>
                <td class="comma-sep">:</td>
                <td class="price text-right px-2">{{ totalVAT }}</td>
              </tr>
              <tr>
                <td class="product px-2">Total Trade Discount</td>
                <td class="comma-sep">:</td>
                <td class="price text-right px-2">{{ getAmountAfterDiscount }}</td>
              </tr>
              <tr>
                <td class="product px-2">Total Bill Amount</td>
                <td class="comma-sep">:</td>
                <td class="price text-right px-2">{{ total }}</td>
              </tr>
              <tr>
                <td class="product px-2 d-flex w-100">
                  Service Charge
                  <div class="price-adj">
                    <span class="mr-5"></span>
                    <input
                      v-model.trim="serviceChargePercent"
                      type="number"
                      class="form-control form-control-sm discount-adj ml-auto text-right"
                      placeholder="%"
                      :min="0"
                    />
                  </div>
                </td>
                <td class="comma-sep">:</td>
                <td class="price">
                  <div class="price-adj px-2">
                    <span class="mr-5"></span>
                    <input
                      v-model.trim="serviceChargeAmount"
                      type="number"
                      class="form-control form-control-sm discount-adj ml-auto text-right"
                      placeholder="amount"
                      :min="0"
                    />
                  </div>
                </td>
              </tr>
              <tr v-if="serviceChargeAmount > 0">
                <td class="product px-2">Select Service Charge Head</td>
                <td class="comma-sep">:</td>
                <td style="font-weight: normal">
                  <v-select
                    placeholder="Select Service Charge Head"
                    v-model="formData.service_charge_head_id"
                    :options="salesAccountHeads"
                    label="name"
                    :reduce="(name) => name.id"
                  />
                </td>
              </tr>
              <tr>
                <td class="product px-2 d-flex w-100">
                  Discount / Round up Adj
                  <div class="price-adj">
                    <span class="mr-5"></span>
                    <input
                      v-model.trim="discountPercent"
                      type="number"
                      class="form-control form-control-sm discount-adj ml-auto text-right"
                      placeholder="%"
                      :min="0"
                    />
                  </div>
                </td>
                <td class="comma-sep">:</td>
                <td class="price">
                  <div class="price-adj px-2">
                    <span class="mr-5"></span>
                    <input
                      v-model.trim="discountAmount"
                      type="number"
                      class="form-control form-control-sm discount-adj ml-auto text-right"
                      placeholder="amount"
                      :min="0"
                    />
                  </div>
                </td>
              </tr>
              <tr v-if="discountAmount > 0">
                <td class="product px-2">Select discount head</td>
                <td class="comma-sep">:</td>
                <td style="font-weight: normal">
                  <v-select
                    placeholder="Select Discount Head"
                    v-model="formData.discount_head_id"
                    :options="discountAccountHeads"
                    label="name"
                    :reduce="(name) => name.id"
                  />
                </td>
              </tr>
              <tr>
                <td class="product px-2">Net Bill</td>
                <td class="comma-sep">:</td>
                <td class="price text-right px-2">{{ grandTotal }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <BillDentitionModal 
      v-if="store.state.isModalOpen" 
      :singleItem="singleItem" 
      @onSave="onSaveDentition"
    />
  </div>
</template>

<script setup>
import RowInline from '@/components/molecule/company/dental-os/service/billing/DentalTableRow.vue';
import useDetectOutsideClick from '@/services/useDetectOutsideClick';
import { computed, ref, watch, inject } from 'vue';
import MoneyReceiptForm from '@/components/molecule/company/dental-os/service/billing/MoneyReceiptForm.vue';
import { useRoute } from 'vue-router';
import BillDentitionModal from '@/components/molecule/company/dental-os/service/middle-header/BillDentitionModal.vue';
import { useStore } from 'vuex';

const route = useRoute();
const store = useStore();
const tableRef = ref(null);
const discountAmount = ref(0);
const serviceChargeAmount = ref(0);
const discountPercent = ref(0);
const serviceChargePercent = ref(0);
const totals = inject('totals');
const formData = inject('formData');
const singleItem = ref(null);

const props = defineProps({
  tableItems: {
    type: Array
  },
  addNewItem: {
    type: Function
  },
  salesAccountHeads: {
    type: Array
  },
  discountAccountHeads: {
    type: Array,
    default: []
  },
  vatRate: {
    type: Array
  },
  subTotalVal: {
    type: Number
  },
  totalVatVal: {
    type: Number
  },
  totalVal: {
    type: Number
  },
  isHideAddBtn: {
    type: Boolean,
    default: false
  },
  from: {
    type: String,
    default: ''
  }
});

const onSaveDentition = () => {
  if (props.from !== 'add') return;
  singleItem.value.quantity = singleItem.value.dentition_pertains.filter((item) => item.is_billed).length || 1;
}

const grandTotal = computed(
  () => total.value - discountAmount.value + serviceChargeAmount.value
);

watch(discountAmount, (value) => {
  formData.value.adjustment_amount = discountAmount.value;
  formData.value.discount_head_id = getDiscountAccountHead();
});

const getDiscountAccountHead = () => {
  if (
    discountAmount.value > 0 &&
    props.discountAccountHeads &&
    props.discountAccountHeads.length
  ) {
    return props.discountAccountHeads[0].id;
  }

  return null;
};

const openDentitionModal = (item) => {
  singleItem.value = item;
  store.state.isModalOpen = true;
};

const getSalesAccountHead = () => {
  if (
    serviceChargeAmount.value > 0 &&
    props.salesAccountHeads &&
    props.salesAccountHeads.length
  ) {
    return props.salesAccountHeads[0].id;
  }

  return null;
};

watch(serviceChargeAmount, (value) => {
  formData.value.service_charge_amount = serviceChargeAmount.value;
  formData.value.service_charge_head_id = getSalesAccountHead();
});

watch(discountPercent, (value) => {
  if (! discountPercent.value) {
    return (discountAmount.value = 0);
  }
  return (discountAmount.value = (total.value * discountPercent.value) / 100);
});

watch(serviceChargePercent, (value) => {
  return (serviceChargeAmount.value = (total.value * serviceChargePercent.value) / 100);
});

const isSelectedForBilling = (item) => {
  if (item.has_sales_general) return true;
  if (! item.is_billed) return item.is_checked;
  return false;
}

const subTotal = computed(() => {
  let subTotal = 0;

  props.tableItems.forEach((item) => {
    if (! isSelectedForBilling(item)) return;
    subTotal += parseInt((item.quantity * item.rate) - item.discount_amount);
  });

  totals.value.subTotalVal = subTotal;
  return subTotal;
});

const totalVAT = computed(() => {
  let vat = 0;

  props.tableItems.forEach((item) => {
    if (! isSelectedForBilling(item)) return;
    if (item.vat_amount) {
      vat += parseFloat(item.vat_amount);
    }
  });

  totals.value.totalVatVal = vat;
  return vat.toFixed(2);
});

const getAmountAfterDiscount = computed(() => {
  let discount = 0;

  props.tableItems.forEach((item) => {
    if (! isSelectedForBilling(item)) return;
    if (item.discount_amount) {
      discount += parseFloat(item.discount_amount);
    }
  });

  return discount;
});

const total = computed(() => {
  var totalValue = parseInt(subTotal.value) + parseInt(totalVAT.value);
  totals.value.totalVal = totalValue;
  return totalValue;
});

const emit = defineEmits(['addNewItem']);

useDetectOutsideClick(tableRef, () => {
  resetItemList();
});

const onOpenEditItem = (index) => {
  resetItemList(index);
};

const onDeleteItem = (index) => {
  if (! confirm('Are you sure to delete this item?')) {
    return;
  }
  props.tableItems.splice(index, 1);
};

const resetItemList = (selectedIndex = -1) => {
  for (let i = 0; i < props.tableItems.length; i++) {
    if (selectedIndex === i) {
      props.tableItems[i].is_edit = true;
      continue;
    }
    props.tableItems[i].is_edit = false;
  }
};
</script>

<style>
.price-adj {
  display: flex;
  align-items: center;
}

.discount-adj {
  width: 60%;
}

.table-totals {
  font-weight: bold;
}

.product {
  width: 60%;
}

.comma-sep {
  text-align: right;
}

.particular {
  width: 25%;
}

.qty {
  width: 6%;
}

.discount {
  width: 5%;
}

.card-height {
  min-height: 200px;
}

.add-new-line {
  margin-top: 4%;
}

.th-action {
  width: 5%;
}
</style>
